<template>
  <div class="about bg-white min-h-screen">
    <!-- Header -->
    <div class="bg-white/90 backdrop-blur-sm shadow-2xl border border-black/20 rounded-lg mx-4 my-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 class="text-3xl font-bold text-black mb-2">About Vue 3 + Vite App</h1>
        <p class="text-gray-600">Modern web application with the latest features and best practices</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Hero Section -->
      <div class="bg-white/90 backdrop-blur-sm shadow-2xl border border-black/20 rounded-lg p-8 mb-8">
        <div class="text-center">
          <div class="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
            <span class="text-white font-bold text-3xl">🚀</span>
          </div>
          <h2 class="text-4xl font-bold text-black mb-4">Vue 3 + Vite App</h2>
          <p class="text-xl text-gray-700 mb-6">
            A modern web application built with Vue 3 and Vite, featuring the latest web development tools and best practices.
          </p>
        </div>
      </div>

      <!-- Features Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
            <span class="text-white font-bold text-xl">⚡</span>
          </div>
          <h3 class="text-xl font-semibold text-black mb-2">Lightning Fast Development</h3>
          <ul class="space-y-2 text-gray-700">
            <li>⚡ Hot Module Replacement (HMR)</li>
            <li>🎨 Modern Vue 3 Composition API</li>
            <li>📱 Fully responsive design</li>
            <li>🔧 TypeScript support</li>
            <li>🎨 Component-based architecture</li>
            <li>🚀 Optimized production builds</li>
          </ul>
        </div>

        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
            <span class="text-white font-bold text-xl">🛠️</span>
          </div>
          <h3 class="text-xl font-semibold text-black mb-2">Tech Stack</h3>
          <ul class="space-y-2 text-gray-700">
            <li><strong>Frontend:</strong> Vue 3, Vite, Tailwind CSS</li>
            <li><strong>Build Tool:</strong> Vite 5.4.21</li>
            <li><strong>Package Manager:</strong> npm 10.2.4</li>
            <li><strong>Language:</strong> JavaScript, TypeScript</li>
            <li><strong>Styling:</strong> Tailwind CSS 3.4.1</li>
            <li><strong>State Management:</strong> Pinia 2.1</li>
            <li><strong>Routing:</strong> Vue Router 4.2.5</li>
          </ul>
        </div>

        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
            <span class="text-white font-bold text-xl">🚀</span>
          </div>
          <h3 class="textxl font-semibold text-black mb-2">Performance</h3>
          <ul class="space-y-2 text-gray-700">
            <li>⚡ Fast development server</li>
            <li>🔥 Optimized production builds</li>
            <li>📦 Lazy loading support</li>
            <li>🎯 Code splitting</li>
            <li>📱 Mobile responsive</li>
            <li>🚀 Tree shaking</li>
          </ul>
        </div>
      </div>

      <!-- Development Workflow -->
      <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-8 mb-8">
        <h3 class="text-2xl font-bold text-black mb-6">Development Workflow</h3>
        <ol class="space-y-4 text-gray-700">
          <li class="flex items-start space-x-3">
            <span class="flex-shrink-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center font-bold">1</span>
            <div>
              <strong>Setup:</strong> Install dependencies with <code class="bg-gray-800 text-white px-2 py-1 rounded text-sm">npm install</code>
            </div>
          </li>
          <li class="flex items-start space-x-3">
            <span class="flex-shrink-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center font-bold">2</span>
            <div>
              <strong>Development:</strong> Run <code class="bg-gray-800 text-white px-2 py-1 rounded text-sm">npm run dev</code> for hot reload
            </div>
          </li>
          <li class="flex items-start space-x-3">
            <span class="flex-shrink-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center font-bold">3</span>
            <div>
              <strong>Build:</strong> Create production build with <code class="bg-gray-800 text-white px-2 py-1 rounded text-sm">npm run build</code>
            </div>
          </li>
          <li class="flex items-start space-x-3">
            <span class="flex-shrink-0 w-8 w-8 bg-black text-white rounded-full flex items-center justify-center font-bold">4</span>
            <div>
              <strong>Deploy:</strong> Deploy with <code class="bg-gray-800 text-white px-2 py-1 rounded text-sm">npm run deploy</code> or use your preferred hosting
            </div>
          </li>
        </ol>
      </div>

      <!-- Quick Links -->
      <div class="bg-gradient-to-r from-black/80 via-purple-900/80 to-blue-900/80 rounded-lg p-8 mb-8">
        <h3 class="text-2xl font-bold text-white mb-6 text-center">Quick Links</h3>
        <div class="grid grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/" class="block text-center text-white hover:bg-white/20 rounded-lg px-4 py-3 transition-all duration-200 transform hover:scale-105">
            <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <span class="text-black font-bold text-xl">🏠</span>
            </div>
            <span class="font-medium">Home</span>
          </a>
          <a href="/dashboard" class="block text-center text-white hover:bg-white/20 rounded-lg px-4 py-3 transition-all duration-200 transform hover:scale-105">
            <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <span class="text-black font-bold text-xl">📊</span>
            </div>
            <span class="font-medium">Dashboard</span>
          </a>
          <a href="/media" class="block text-center text-white hover:bg-white/20 rounded-lg px-4 py-3 transition-all duration-200 transform hover:scale-105">
            <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <span class="text-black font-bold text-xl">📸</span>
            </div>
            <span class="font-medium">Media</span>
          </a>
          <a href="/prompting" class="block text-center text-white hover:bg-white/20 rounded-lg px-4 py-3 transition-all duration-200 transform hover:scale-105">
            <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <span class="text-black font-bold text-xl">🤖</span>
            </div>
            <span class="font-medium">AI Prompting</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// No additional script needed for this component
</script>

<style scoped>
.about {
  /* Custom animations for the aura effect */
}

/* Enhanced hover effects */
.hover-lift {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s ease;
}

/* Transform effects */
.transform {
  transition: transform 0.3s ease;
}

.transform:hover {
  transform: scale(1.05);
}

/* Code styling */
code {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}

/* List styling */
ul {
  list-style: none;
  padding-left: 0;
}

li {
  padding: 0.25rem 0;
}

/* Number styling */
.number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  font-weight: bold;
}
</style>